import logo from '/logo.svg'
import { TextInput, Button, Checkbox, Modal, PasswordInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { Link, useNavigate } from 'react-router-dom'
import { TwoFAModal } from './components'
import { TwoFAModalV2, type BrokerSuccess, type MfaChallenge } from './components/TwoFAModalV2'
import { useMedplum } from '@medplum/react'
import { useState } from 'react'
import { useAppFlags } from '@/hooks/useAppFlags'
import { useClient } from '@/hooks/useClient'
import { hydrateBrokerGrant } from '@/auth/brokerSession'
import { hideNotification, showNotification } from '@mantine/notifications'
import { ProjectMembership } from '@medplum/fhirtypes'
import { IconCheck } from '@tabler/icons-react'
import {
  AUTH_CLASSES,
  AUTH_COLORS,
  AUTH_LOGIN_FIELD_LABEL_STYLES,
  AUTH_LOGIN_CHECKBOX_STYLES,
  AUTH_LOGIN_PASSWORD_INPUT_STYLES,
  AUTH_LOGIN_PRIMARY_BUTTON_STYLES,
  AUTH_LOGIN_TEXT_INPUT_STYLES,
} from './authTheme'

import { config } from '../../config'

type FormProps = {
  email: string
  password: string
  rememberMe: boolean
}

type Profile = {
  key?: string
  value?: string
}

const FEATURES = [
  'Order screenings with insurance pre-authorization',
  'Receive structured results & follow-up alerts',
  'Track HEDIS & quality metrics in real time',
  'Connected to TEFCA™ Network for real-time clinical data access',
]

export const Login = () => {
  const [login, setLogin] = useState<string>()
  const [tempLogin, setTempLogin] = useState<string>()
  const [profiles, setProfiles] = useState<Profile[]>([])
  // Gateway-flow state (PD-1512, behind authV2Provider): opaque challenge ids only.
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | undefined>(undefined)
  const [selectChallengeId, setSelectChallengeId] = useState<string | undefined>(undefined)

  const medplum = useMedplum()
  const navigate = useNavigate()
  const client = useClient()
  const { authV2Provider } = useAppFlags<{ authV2Provider?: boolean }>()

  const navigateAfterLogin = () => {
    const returnTo = sessionStorage.getItem('returnTo')
    sessionStorage.removeItem('returnTo')
    navigate(returnTo || '/', { replace: true })
  }

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
    },
  })

  const resetStates = () => {
    setLogin(undefined)
    setTempLogin(undefined)
    setProfiles([])
    setMfaChallenge(undefined)
    setSelectChallengeId(undefined)
  }

  const finishBrokerLogin = async (grant: BrokerSuccess) => {
    await hydrateBrokerGrant(medplum, grant)
    resetStates()
    navigateAfterLogin()
  }

  // The union from /v2/auth/login and /v2/auth/profile-select drives the same three
  // screens as the legacy flow — but the client only ever holds opaque challenge ids.
  const handleGatewayOutcome = async (
    outcome:
      | BrokerSuccess
      | ({ state: 'mfa' } & MfaChallenge)
      | {
          state: 'membershipSelect'
          challengeId: string
          memberships: {
            id: string
            profileRef?: string
            profileDisplay?: string
            projectName?: string
          }[]
        },
  ) => {
    if (outcome.state === 'success') {
      await finishBrokerLogin(outcome)
      return
    }
    if (outcome.state === 'membershipSelect') {
      setSelectChallengeId(outcome.challengeId)
      setProfiles(
        outcome.memberships.map((m) => ({
          key: m.id,
          // Same field the legacy picker shows — the PROJECT name distinguishes
          // memberships; the profile display is often identical across them.
          value: m.projectName ?? m.profileDisplay ?? m.profileRef ?? m.id,
        })),
      )
      return
    }
    setMfaChallenge(outcome)
  }

  const setLoginHandler = async (login: string) => {
    const check = await medplum.post('auth/2fa/check', {
      login,
    })

    if (check.code) {
      await medplum.processCode(check.code)
      navigateAfterLogin()
      return
    }

    if (check.memberships) {
      setProfiles(
        check.memberships.map((item: ProjectMembership) => ({
          key: item.id,
          value: item.project.display,
        })),
      )
      setTempLogin(login)
      setLogin(undefined)
      return
    }

    setLogin(login)
  }

  const handleSubmit = async (formData: FormProps) => {
    hideNotification('session-expired')

    if (authV2Provider) {
      try {
        const outcome = await client.authLogin({
          email: formData.email,
          password: formData.password,
        })
        await handleGatewayOutcome(outcome)
      } catch (err) {
        showNotification({
          color: 'red',
          message: (err as Error).message || 'Login failed',
        })
      }
      return
    }

    try {
      const response = await medplum.startLogin({
        email: formData.email,
        password: formData.password,
        scope: 'offline_access',
      })

      if (response.login && (response.mfaRequired || response.mfaEnrollRequired)) {
        await setLoginHandler(response.login)
      } else if (response.login && response.memberships?.length) {
        setProfiles(
          response.memberships.map((item) => ({
            key: item.id,
            value: item.project.display,
          })),
        )
        setTempLogin(response.login)
      } else if (response.code) {
        await medplum.processCode(response.code)
        navigateAfterLogin()
      } else {
        showNotification({
          color: 'red',
          message: 'Login failed',
        })
      }
    } catch (err) {
      showNotification({
        color: 'red',
        message: (err as Error).message || 'Login failed',
      })
    }
  }

  const handleProfileSelect = async (profile: Profile) => {
    if (authV2Provider && selectChallengeId) {
      try {
        const outcome = await client.authProfileSelect({
          challengeId: selectChallengeId,
          membershipId: profile.key as string,
        })
        setProfiles([])
        setSelectChallengeId(undefined)
        await handleGatewayOutcome(outcome)
      } catch (err) {
        showNotification({
          color: 'red',
          message: (err as Error).message || 'Login failed',
        })
      }
      return
    }

    const response = await medplum.post('auth/profile', {
      login: tempLogin,
      profile: profile.key,
    })
    if (response.login && (response.mfaRequired || response.mfaEnrollRequired)) {
      await setLoginHandler(response.login)
    } else if (response.code) {
      await medplum.processCode(response.code)
      navigateAfterLogin()
    } else {
      showNotification({
        color: 'red',
        message: 'Login failed',
      })
    }
  }

  const onProfileSelect = (profiles: Profile[]) => {
    setTempLogin(login)
    setLogin(undefined)
    setProfiles(profiles)
  }

  return (
    <div
      className={`flex min-h-screen w-full flex-col ${AUTH_CLASSES.loginPageBg} ${AUTH_CLASSES.fontScope}`}
    >
      <Modal
        opened={!!profiles.length}
        classNames={{ content: '!rounded-xl' }}
        onClose={resetStates}
      >
        <p className="text-base text-[24px] font-bold">Select profile</p>
        {profiles.map((profile) => (
          <Button
            key={profile.key}
            fullWidth
            onClick={() => handleProfileSelect(profile)}
            variant="light"
            size="lg"
            className="mt-4"
          >
            {profile.value}
          </Button>
        ))}
      </Modal>
      <TwoFAModal
        login={login}
        onFinish={() => navigateAfterLogin()}
        onCancel={() => navigate('/login')}
        onProfileSelect={onProfileSelect}
      />
      <TwoFAModalV2
        challenge={mfaChallenge}
        onSuccess={(grant) => void finishBrokerLogin(grant)}
        onCancel={resetStates}
      />

      <header className="flex items-center justify-between px-8 py-5">
        <Link to="/">
          <img src={logo} alt="beHuman" className="block h-9" />
        </Link>
        <p className={`m-0 text-[13.5px] ${AUTH_CLASSES.mutedText}`}>
          Need help?{' '}
          <a
            href={`${config.marketingSiteUrl}/contact`}
            className={`font-medium ${AUTH_CLASSES.accentLink}`}
          >
            Contact us
          </a>
        </p>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 pt-10 pb-20">
        <div className="grid w-full max-w-[920px] grid-cols-1 overflow-hidden rounded-[28px] shadow-[0_8px_48px_rgba(0,0,0,0.13)] md:grid-cols-2">
          <div
            className={`relative hidden flex-col justify-between px-12 py-14 md:flex ${AUTH_CLASSES.loginHeroGradient}`}
          >
            <div>
              <img src={logo} alt="BeHuman" className="mb-10 h-8 brightness-0 invert" />

              <div
                className={`mb-7 inline-flex items-center gap-2 rounded-full px-3.5 py-[5px] ${AUTH_CLASSES.accentPill}`}
              >
                <span className={`block h-1.5 w-1.5 rounded-full ${AUTH_CLASSES.accentDot}`} />
                <span
                  className={`text-[11px] font-bold tracking-[0.1em] uppercase ${AUTH_CLASSES.accentText}`}
                >
                  Provider Portal
                </span>
              </div>

              <h1
                className={`mb-5 text-[clamp(1.8rem,2.5vw,2.3rem)] leading-[1.25] text-white ${AUTH_CLASSES.serifText}`}
              >
                Screen more patients.
                <br />
                <em className={`italic ${AUTH_CLASSES.accentText}`}>Save more lives.</em>
              </h1>
              <p className="text-[14.5px] leading-[1.7] text-white/60">
                Coordinate covered cancer screenings for your entire panel — ordering, results, and
                follow-up in one clinical dashboard.
              </p>
            </div>

            <ul className="mt-12 flex flex-col gap-4">
              {FEATURES.map((text) => (
                <li key={text} className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${AUTH_CLASSES.accentBadge}`}
                  >
                    <IconCheck size={12} stroke={3} />
                  </span>
                  <span className="text-[13px] leading-[1.5] text-white/65">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col justify-center bg-white px-8 py-12 md:px-12 md:py-14">
            <img src={logo} alt="BeHuman" className="mb-8 h-9 md:hidden" />

            <p
              className={`mb-3 text-[11px] font-bold tracking-[0.12em] uppercase ${AUTH_CLASSES.accentText}`}
            >
              Provider Portal
            </p>
            <h2
              className={`mb-11 text-[32px] leading-[1.2] text-[#1a1a1a] ${AUTH_CLASSES.serifText}`}
            >
              Provider sign in
            </h2>

            <form className="flex flex-col gap-4" onSubmit={form.onSubmit(handleSubmit)}>
              <TextInput
                label="Work email"
                type="email"
                placeholder="you@practice.com"
                autoComplete="email"
                styles={AUTH_LOGIN_TEXT_INPUT_STYLES}
                classNames={{ input: AUTH_CLASSES.inputFocus }}
                {...form.getInputProps('email')}
              />

              <div>
                <div className="mb-[7px] flex items-baseline justify-between">
                  <label
                    htmlFor="login-password"
                    style={{
                      fontSize: AUTH_LOGIN_FIELD_LABEL_STYLES.fontSize,
                      fontWeight: AUTH_LOGIN_FIELD_LABEL_STYLES.fontWeight,
                      color: AUTH_LOGIN_FIELD_LABEL_STYLES.color,
                      letterSpacing: AUTH_LOGIN_FIELD_LABEL_STYLES.letterSpacing,
                      lineHeight: 'normal',
                    }}
                  >
                    Password
                  </label>
                  <Link
                    to="/resetpassword"
                    className={`text-[12px] ${AUTH_CLASSES.accentText} hover:underline`}
                    style={{
                      fontSize: AUTH_LOGIN_FIELD_LABEL_STYLES.fontSize,
                      fontWeight: AUTH_LOGIN_FIELD_LABEL_STYLES.fontWeight,
                      lineHeight: 'normal',
                    }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="login-password"
                  name="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  styles={AUTH_LOGIN_PASSWORD_INPUT_STYLES}
                  classNames={{ input: AUTH_CLASSES.passwordFocus }}
                  {...form.getInputProps('password')}
                />
              </div>

              <Checkbox
                label="Remember me"
                size="xs"
                color={AUTH_COLORS.accent}
                iconColor="#fff"
                styles={AUTH_LOGIN_CHECKBOX_STYLES}
                {...form.getInputProps('rememberMe', { type: 'checkbox' })}
              />

              <Button
                type="submit"
                disabled={!(form.values.email && form.values.password)}
                loading={form.submitting}
                fullWidth
                size="md"
                styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
                classNames={{ root: AUTH_CLASSES.primaryButtonState }}
              >
                Sign in
              </Button>
            </form>

            <div
              className={`mt-8 flex items-center justify-between gap-3 rounded-2xl px-5 py-4 ${AUTH_CLASSES.patientCard}`}
            >
              <p
                className="m-0 text-[12.5px] leading-[1.5]"
                style={{ color: AUTH_COLORS.softText }}
              >
                Are you a patient?
              </p>
              {config.patientPortalUrl && (
                <a
                  href={`${config.patientPortalUrl?.replace(/\/$/, '')}/login`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`shrink-0 text-[12.5px] font-semibold whitespace-nowrap ${AUTH_CLASSES.accentLink}`}
                >
                  Patient login →
                </a>
              )}
            </div>

            <p
              className="mt-6 text-center text-[11.5px] leading-[1.6]"
              style={{ color: AUTH_COLORS.footerText }}
            >
              By signing in you agree to our{' '}
              <a
                href={`${config.marketingSiteUrl}/terms-of-use`}
                className={AUTH_CLASSES.mutedUnderlineLink}
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href={`${config.marketingSiteUrl}/privacy-policy`}
                className={AUTH_CLASSES.mutedUnderlineLink}
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
